// useVehicleOptions.js
import { useState, useEffect } from 'react';

// 缓存选项数据
let cachedOptions = null;
let isLoading = false;
let loadError = null;
// 等待加载的回调队列
const pendingPromises = [];
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const defaultOptions = {
  type: [{ value: 'none', label: 'Select Type' }],
  color: [{ value: 'none', label: 'Select Color' }],
  model: [{ value: 'none', label: 'Select Model' }],
  stickers: [{ value: 'none', label: 'Select Stickers' }],
  bumper: [{ value: 'none', label: 'Select Bumper' }]
};

/**
 * 获取车辆筛选选项的Hook，带全局缓存
 * @returns {Object} { options, isLoading, error, refreshOptions }
 */
export const useVehicleOptions = () => {
  const [options, setOptions] = useState(cachedOptions);
  const [loading, setLoading] = useState(!cachedOptions && isLoading);
  const [error, setError] = useState(loadError);

  
  useEffect(() => {
    const fetchOptions = async () => {
      // 如果已经有缓存数据，直接使用
      if (cachedOptions) {
        setOptions(cachedOptions);
        setLoading(false);
        return;
      }
      
      // 如果正在加载中，等待加载完成
      if (isLoading) {
        const promise = new Promise((resolve, reject) => {
          pendingPromises.push({ resolve, reject });
        });
        
        promise
          .then(data => {
            setOptions(data);
            setLoading(false);
            setError(null);
          })
          .catch(err => {
            setError(err);
            setLoading(false);
          });
        
        return;
      }
      
      // 开始新的加载过程
      isLoading = true;
      setLoading(true);
      
      console.log('开始从后端获取车辆选项数据...');
      try {
        const response = await fetch('${API_BASE_URL}/api/options/vehicle');
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const backendData = await response.json();
        console.log('成功从后端获取原始车辆选项数据:', backendData);
    
        const formattedOptions = transformOptionsForUI(backendData);
        console.log('格式化后的车辆选项数据:', formattedOptions);
        
        // 更新缓存
        cachedOptions = formattedOptions;
        loadError = null;
        
        // 更新组件状态
        setOptions(formattedOptions);
        setError(null);
        
        // 通知所有等待的Promise
        pendingPromises.forEach(p => p.resolve(formattedOptions));
        pendingPromises.length = 0;
      } catch (err) {
        console.error('Error fetching vehicle options:', err);
        
        // 记录错误
        loadError = err.message;
        
        // 更新组件状态
        setError(err.message);
        
        // 通知所有等待的Promise
        pendingPromises.forEach(p => p.reject(err));
        pendingPromises.length = 0;
        
        // 使用空选项
        const emptyOptions = getEmptyOptions();
        setOptions(emptyOptions);
      } finally {
        isLoading = false;
        setLoading(false);
      }
    };
    
    fetchOptions();
  }, []);
  
  // 提供手动刷新选项的函数
  const refreshOptions = async () => {
    // 清除缓存
    cachedOptions = null;
    loadError = null;
    isLoading = false;
    pendingPromises.length = 0;
    
    // 重新加载
    setLoading(true);
    
    try {
      const response = await fetch('${API_BASE_URL}/api/options/vehicle');
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const backendData = await response.json();
      const formattedOptions = transformOptionsForUI(backendData);
      
      // 更新缓存
      cachedOptions = formattedOptions;
      
      // 更新组件状态
      setOptions(formattedOptions);
      setError(null);
    } catch (err) {
      console.error('Error refreshing vehicle options:', err);
      setError(err.message);
      loadError = err.message;
      
      // 使用空选项
      const emptyOptions = getEmptyOptions();
      setOptions(emptyOptions);
    } finally {
      setLoading(false);
    }
  };
  
  return { options, isLoading: loading, error, refreshOptions };
};

/**
 * 获取空选项结构
 * @returns {Object} 仅包含'none'选项的空结构
 */
function getEmptyOptions() {
  return {
    type: [{ value: 'none', label: 'Select Type' }],
    color: [{ value: 'none', label: 'Select Color' }],
    model: [{ value: 'none', label: 'Select Model' }],
    stickers: [{ value: 'none', label: 'Select Stickers' }],
    bumper: [{ value: 'none', label: 'Select Bumper' }]
  };
}

/**
 * 将后端选项格式转换为前端使用的格式
 * @param {Object} backendOptions - 后端返回的选项数据
 * @returns {Object} 适合前端使用的选项格式
 */

function transformOptionsForUI(backendOptions) {
    // 初始化UI选项对象，包含'none'选项
    const uiOptions = {
      type: [{ value: 'none', label: 'Select Type' }],
      color: [{ value: 'none', label: 'Select Color' }],
      model: [{ value: 'none', label: 'Select Model' }],
      stickers: [{ value: 'none', label: 'Select Stickers' }],
      bumper: [{ value: 'none', label: 'Select Bumper' }]
    };
    
    try {
      console.log('原始后端车辆选项数据类型:', typeof backendOptions, Array.isArray(backendOptions));
      
      // 情形1: 后端返回对象格式 {feature: [{value, label}, ...], ...}
      if (backendOptions && typeof backendOptions === 'object' && !Array.isArray(backendOptions)) {
        Object.entries(backendOptions).forEach(([feature, options]) => {
          if (uiOptions[feature]) {
            if (Array.isArray(options)) {
              options.forEach(option => {
                // 如果选项已经是{value,label}格式
                if (option && typeof option === 'object' && option.value) {
                  if (!uiOptions[feature].some(opt => opt.value === option.value)) {
                    uiOptions[feature].push(option);
                  }
                } 
                // 如果选项只是字符串
                else if (typeof option === 'string') {
                  if (!uiOptions[feature].some(opt => opt.value === option)) {
                    uiOptions[feature].push({
                      value: option,
                      label: capitalizeFirstLetter(option)
                    });
                  }
                }
              });
            } 
          }
        });
      }
      // 情形2: 后端返回数组格式 (可能是车辆列表)
      else if (Array.isArray(backendOptions)) {
        backendOptions.forEach(vehicle => {
          if (vehicle && vehicle.features) {
            Object.entries(vehicle.features).forEach(([feature, value]) => {
              if (uiOptions[feature] && value) {
                if (!uiOptions[feature].some(opt => opt.value === value)) {
                  uiOptions[feature].push({
                    value: value,
                    label: capitalizeFirstLetter(value)
                  });
                }
              }
            });
          }
        });
      }
      
      // 对每个特征类别的选项排序
      Object.keys(uiOptions).forEach(feature => {
        const noneOption = uiOptions[feature].find(opt => opt.value === 'none');
        const otherOptions = uiOptions[feature]
          .filter(opt => opt.value !== 'none')
          .sort((a, b) => a.label.localeCompare(b.label));
        
        uiOptions[feature] = [noneOption, ...otherOptions];
      });
      
      console.log('转换后的车辆选项:', uiOptions);
    } catch (error) {
      console.error('转换车辆选项时出错:', error, error.stack);
    }
    
    return uiOptions;
  }
  
  // 首字母大写辅助函数
  function capitalizeFirstLetter(string) {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  }